import type { RemoteName } from "../../mfe/mfeConfig";
import type {
  IsRouteEnabledOptions,
  RouteConfigMenu,
  LegacyRouteConfig,
  RouteConfigBase,
  LegacyRouteConfigSubMenu,
} from "../../navigationConfig";
import type { PartialRequired } from "../../types";
import { getCheckRoutes } from "./getCheckRoutes";
import type { MfeRouteConfig } from "./types";

/**
 * Shamelessly stolen from:
 * https://github.com/remix-run/react-router/blob/v6.4.0-pre.2/packages/router/utils.ts#L441-L544
 * and made a lot simpler for our use case.
 */
export function matchPath<Path extends string>(
  path: Path,
  pathname: string
): boolean {
  return !!pathname.match(compilePath(path));
}

function compilePath(path: string): RegExp {
  if (path !== "*" && path.endsWith("*") && !path.endsWith("/*")) {
    console.warn(
      `Route path "${path}" will be treated as if it were ` +
        `"${path.replace(/\*$/, "/*")}" because the \`*\` character must ` +
        "always follow a `/` in the pattern. To get rid of this warning, " +
        `please change the route path to "${path.replace(/\*$/, "/*")}".`
    );
  }
  let regexpSource =
    "^" +
    path
      .replace(/\/*\*?$/, "") // Ignore trailing / and /*, we'll handle it below
      .replace(/^\/*/, "/") // Make sure it has a leading /
      .replace(/[\\.*+^${}|()[\]]/g, "\\$&") // Escape special regex chars
      .replace(/\/:(\w+)(\?)?/g, (_: string, _paramName: string, isOptional) =>
        isOptional ? "/?([^\\/]+)?" : "/([^\\/]+)"
      );

  if (path.endsWith("*")) {
    regexpSource +=
      path === "*" || path === "/*"
        ? "(.*)$" // Already matched the initial /, just match the rest
        : "(?:\\/(.+)|\\/*)$"; // Don't include the / in params["*"]
  } else {
    // When matching to the end, ignore trailing slashes
    regexpSource += "\\/*$";
  }

  return new RegExp(regexpSource, "i");
}

type FallbackRoute = RouteConfigBase & { path: "/*" };
export type RouteConfigWithFallback = [...RouteConfigBase[], FallbackRoute];
export const getRouterConfig = (
  // Ensures that a route to fallback on is defined
  routeConfig: RouteConfigWithFallback,
  isEnabledOption?: IsRouteEnabledOptions
): Record<RemoteName, MfeRouteConfig> => {
  const routableRoutes = getRoutableRoutes(routeConfig, isEnabledOption);

  return routableRoutes.reduce(
    (acc, route): Record<RemoteName, MfeRouteConfig> => {
      const existingEntry = acc[route.mfe.name];

      if (!existingEntry) {
        return {
          ...acc,
          [route.mfe.name]: {
            ...("menu" in route ? { textKey: route.menu.textKey } : {}),
            paths: [route.path],
            import: route.import,
            name: route.mfe.name,
            entry: route.mfe.entry,
          },
        };
      }

      return {
        ...acc,
        [route.mfe.name]: {
          ...existingEntry,
          ...("menu" in route ? { textKey: route.menu.textKey } : {}),
          paths: [...existingEntry.paths, route.path],
        },
      };
    },
    {} as Record<RemoteName, MfeRouteConfig>
  );
};

export const getRoutableRoutes = (
  routesConfig: (LegacyRouteConfig | LegacyRouteConfigSubMenu)[],
  isEnabledOption?: IsRouteEnabledOptions
): LegacyRouteConfig[] => {
  const checkRoutes = getCheckRoutes(isEnabledOption);
  const authorizedMainRoutes = routesConfig.filter(checkRoutes);
  const authorizedSubRoutes = authorizedMainRoutes
    .flatMap((x) => {
      if ("menu" in x) {
        if ("subRoutes" in x.menu) return x.menu.subRoutes;
        if ("subRouteSections" in x.menu) {
          const mmRoute = x as unknown as RouteConfigMenu;
          return (
            mmRoute?.menu?.subRouteSections?.flatMap(
              (section) => section.subRoutes
            ) || []
          );
        }
      }
      return [];
    })
    .filter(checkRoutes);

  const allRoutes = [...authorizedMainRoutes, ...authorizedSubRoutes];

  return allRoutes.filter(
    (route): route is PartialRequired<typeof route, "mfe" | "key"> =>
      !!route.key && !!route.mfe
  );
};

export const matchPatterns = (pathname: string, patterns: string[]) =>
  patterns.some((p) => matchPath(p, pathname));
